import React, { useContext, useEffect, useState } from 'react';
import { Button, Grid, TextField, TableCell, TableRow, TableContainer, Table, TableBody, DialogActions, Box, Checkbox, DialogContent, Dialog, DialogTitle, IconButton, Alert } from '@mui/material';
import { UserContext } from '../../../../../components/shared/useUser';
import useNswagClient from '../../../../../hooks/api/useNswagClient';
import { GetItemsResponse, Item, PostStockCheckDoNotCountItemRequest } from '../../../../../app/services/api/generated';
import { SearchSm } from '../../../../../assets';
import CustomPagination from '../../../../../components/forms/Pagination';
import useLogError from '../../../../../hooks/useLogError';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { TableLoading } from '../../../../../components/forms/TableLoading';
import { TableHeader } from '../../../../../components/forms/TableHeader';
import { Pill } from '../../../../../components/shared/Pill';
import { EmptyTable } from '../../../../../components/shared/EmptyTable';
import { EmptyRows } from '../../../../../components/forms/EmptyRows';

interface NotAddedItem {
  itemNumber: string;
  reason: string;
}

interface ExtendedGetItemsResponse extends GetItemsResponse {
  notAddedItems?: NotAddedItem[];
}

const DoNotCountItemsAddItemsModal = (props: { isItemsModalOpen: boolean, handleClose: () => void, reloadData: () => void }) => {
  const { t } = useTranslation('doNotCountItems');
  const { selectedStore } = useContext(UserContext);
  const { getItems, addDoNotCountItems } = useNswagClient();
  const { logError } = useLogError();

  const [apiData, setApiData] = useState<ExtendedGetItemsResponse | undefined>();
  const [tempData, setTempData] = useState<Item[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState<number | undefined>();
  const [notAddedItems, setNotAddedItems] = useState<NotAddedItem[]>([]);

  const rowsPerPage = 10;

  useEffect(() => {
    if (props.isItemsModalOpen) {
      setSearch('');
      setPage(0);
      setApiData(undefined);
      setCount(0);
      setNotAddedItems([]);
      setTempData([]);
      getItemData('');
    }
  }, [props.isItemsModalOpen]);

  useEffect(() => {
    getItemData();
  }, [page]);

  const getItemData = (searchOverride?: string) => {
    setIsLoading(true);
    const skip = rowsPerPage * page;
    const searchToUse = searchOverride !== undefined ? searchOverride : search;
    const storeNumber = selectedStore?.storeNumber === undefined ? '' : selectedStore.storeNumber;
    getItems(searchToUse, storeNumber, undefined, undefined, true, undefined, undefined, undefined, undefined, undefined, true, '', '', skip, rowsPerPage).then((result) => {
      if (result) {
        const data = result.data as ExtendedGetItemsResponse;
        setApiData(data);
        setCount(data?.itemCount);
        setNotAddedItems(searchToUse.trim() ? (data?.notAddedItems ?? []) : []);
      } else {
        logError(result);
      }
    })
      .catch((error) => {
        logError(error);
      })
      .finally(() => setIsLoading(false));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    getItemData();
  };

  const addItemToTempList = (item: Item) => {
    if (!tempData?.some(e => e.id == item.id)) {
      setTempData([...tempData ?? [], item]);
    }
  };

  const handleConfirm = () => {
    setIsSaving(true);
    const body: PostStockCheckDoNotCountItemRequest = { franchiseId: selectedStore?.franchiseId, itemNumber: tempData.map(item => item.itemNumber ?? '') };
    addDoNotCountItems(body)
      .catch((e) => console.error(e))
      .finally(() => {
        setIsSaving(false);
        props.reloadData();
        close();
      });
  };

  const handleItemUnchecked = (item: number) => {
    setTempData(prevData => prevData?.filter(row => row.id !== item) || []);
  };

  const handleItemChecked = (item: number) => {
    const selecteditem = apiData?.items?.find(row => row.id === item);
    if (selecteditem) {
      addItemToTempList(selecteditem);
    }
  };

  const close = () => {
    setTempData([]);
    !isSaving && props.handleClose();
  };

  const maxPages = () => count ? Math.ceil(count / rowsPerPage) : 0;

  return (
    <Dialog open={props.isItemsModalOpen}
      onClose={close}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('itemsLookup')}
        <IconButton
          onClick={close}
          sx={{
            position: 'absolute',
            right: 16,
            top: 12,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
          display='flex'
          flexWrap={'nowrap'}
          alignItems='self-end'
        >
          <Grid item
            xs={10}
            sx={{ pt: '5px' }}>
            <TextField
              fullWidth
              placeholder={t('search')}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}
              value={search}
              onChange={handleSearchChange} />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Button size='lg'
              disabled={isLoading}
              startIcon={<SearchSm />}
              onClick={handleSearch} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          {isSaving ? <Alert severity='info'>{t('addProccessingMessage')}</Alert> :
            <>
              {count == 0 && !isLoading ? <Section><EmptyTable bodyRowHeight={40}
                headerHeight={1}
                text={t('Items')}
                searchTerm={search}
                onClear={() => { }}
                onAdd={() => { }}
                onAddIsCreating={false}
                addItemText={''}
                showClear={false}
                showOnAdd={false}
                onAddIsDisabled={false} />
              </Section>
                :
                <Section>
                  <TableHeader>
                    {t('Items')}
                    {(count ?? 0) > 0 && <Pill text={`${count} ${t('items')}`}></Pill>}
                  </TableHeader>
                  {isLoading ? <TableLoading headerHeight={1}
                    bodyRowHeight={40}
                    minTableWidth={950} /> :
                    <TableContainer>
                      <Table>
                        <TableBody>
                          {apiData?.items?.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell style={{ display: 'flex', alignItems: 'center' }} >
                                <Checkbox
                                  color="primary"
                                  checked={tempData?.some((item) => item.itemNumber === row.itemNumber)}
                                  onChange={(e) => {
                                    e.target.checked ? handleItemChecked(row.id!) : handleItemUnchecked(row.id!);
                                  }}
                                />
                                <Grid style={{ marginLeft: '8px' }}>
                                  {row.itemNumber}< br />
                                  {row.description}
                                </Grid>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <EmptyRows numberOfCurrentRows={apiData?.items?.length ?? 0}
                        height={40} />
                    </TableContainer>}
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    maxPages={maxPages()}
                    useNoDataMessage={false}
                  />
                </Section>
              }
            </>
          }
        </Box>
        {search.trim() && notAddedItems.length > 0 && (
          <Alert severity='warning'
            sx={{ mt: 2 }}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {notAddedItems.map((item, index) => (
                <li key={`${index}-${item.itemNumber}`}>
                  {item.itemNumber} - {item.reason}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </DialogContent>
      <DialogActions >
        <Button
          variant="secondary"
          size='lg'
          onClick={close}>
          {t('cancel')}
        </Button>
        <Button
          size='lg'
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoNotCountItemsAddItemsModal;